import React, { Fragment } from 'react'
import PropTypes from 'prop-types'

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { X as CloseIcon } from 'react-feather'

import constants from 'constants/index'
import useStyles from './styles'

const BaseDialog = ({
  actionButton = () => {},
  actionButtonText = '',
  children,
  closeButtonText = '',
  closeIconButton = false,
  companySupplier,
  dialogActions,
  dialogSize = 'sm',
  disableButton = false,
  formRef,
  fullWidth = false,
  justifyActions = 'flex-end',
  loading = false,
  mixpanelTrack,
  open,
  resetFields,
  setOpen = () => {},
  onClose,
  tag = '',
  title = '',
  withoutBottomDivider = false,
  preventCloseOnBackdropClick = false,
  closeButtonAction = null,
  noPadding = false,
  noDialogTitle = false,
  accentColor = null, // cor da borda superior do Paper — aplica borderTop: 4px solid + borderRadius: 12 + overflow: hidden
  paperStyle = {}, // estilos extras no Paper — sobrescreve os do accentColor (ex: { borderRadius: 20 })
  headerActions = null, // node renderizado à direita do título, antes do botão de fechar
  titleIcon = null, // node renderizado à esquerda, antes do título
}) => {
  const classes = useStyles({ tag, closeIconButton })
  const newTag = tag === constants.lastUpdates.KINDS_LABELS.N

  const handleClose = (_, reason) => {
    if ((preventCloseOnBackdropClick && reason === 'backdropClick') || loading)
      return
    if (companySupplier?.canResendSolicitation === true) mixpanelTrack()
    if (resetFields) resetFields(null)

    if (!!onClose) return onClose()

    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={dialogSize}
      fullWidth={!!fullWidth}
      PaperProps={{
        className: classes.dialogRoot,
        style: {
          ...(accentColor && {
            border: 'none',
            borderRadius: 12,
            overflow: 'hidden',
            borderTop: `4px solid ${accentColor}`,
          }),
          ...paperStyle,
        },
      }}
    >
      {noDialogTitle ? (
        ''
      ) : (
        <DialogTitle
          disableTypography
          classes={{ root: classes.muiDialogTitleOverride }}
        >
          <Box className={classes.boxTitle}>
            <Box className={classes.boxTitleTwo}>
              {titleIcon && (
                <Box display="flex" alignItems="center" mr={1}>
                  {titleIcon}
                </Box>
              )}
              {tag && (
                <Box>
                  <Chip
                    className={newTag ? classes.new : classes.improvement}
                    label={tag}
                  />
                </Box>
              )}
              <Box>
                <Tooltip
                  title={title}
                  placement="top"
                  disableHoverListener={!tag}
                >
                  <Typography className={classes.title} variant="h4">
                    {title}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              {headerActions}
              {closeIconButton && (
                <IconButton edge="end" onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </DialogTitle>
      )}

      <Divider />
      <DialogContent
        className={classes.dialogContent}
        style={{
          padding: noPadding ? '0' : tag ? '10px 40px' : '24px',
          ...(noPadding && { overflow: 'hidden' }),
        }}
      >
        {children}
      </DialogContent>
      {!withoutBottomDivider && (
        <>
          <Divider />
          <DialogActions>
            <Box display="flex" justifyContent={justifyActions} width="100%">
              {dialogActions ?? (
                <Fragment>
                  {closeButtonText && (
                    <Box pl={2} pr={1}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={
                          closeButtonAction ? closeButtonAction : handleClose
                        }
                        disabled={loading}
                      >
                        {closeButtonText}
                      </Button>
                    </Box>
                  )}
                  {actionButtonText && (
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      onClick={actionButton}
                      disabled={loading || disableButton}
                      form={formRef}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        actionButtonText
                      )}
                    </Button>
                  )}
                </Fragment>
              )}
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

BaseDialog.propTypes = {
  actionButton: PropTypes.func,
  actionButtonText: PropTypes.string,
  children: PropTypes.node.isRequired,
  closeButtonText: PropTypes.string,
  closeIconButton: PropTypes.bool,
  companySupplier: PropTypes.object,
  dialogActions: PropTypes.node,
  dialogSize: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs']),
  disableButton: PropTypes.bool,
  formRef: PropTypes.string,
  fullWidth: PropTypes.bool,
  justifyActions: PropTypes.string,
  loading: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func,
  tag: PropTypes.string,
  title: PropTypes.string,
  noPadding: PropTypes.bool,
  withoutBottomDivider: PropTypes.bool,
  headerActions: PropTypes.node,
  titleIcon: PropTypes.node,
  accentColor: PropTypes.string,
  paperStyle: PropTypes.object,
}

export default BaseDialog
